import React from 'react';
import {
  PROTOCOL_STATUS_LABEL,
  PROTOCOL_TYPE_LABEL,
  VEHICLE_STATUS_LABEL,
  VEHICLE_USAGE_LABEL,
  formatDate,
  formatDateTime,
  formatKm,
  type ProtocolStatus,
  type ProtocolType,
  type VehicleStatus,
  type VehicleUsage,
} from '@apleona/shared';

export const ApleonaLogo: React.FC<{ color?: string }> = ({ color = '#e73638' }) => (
  <svg viewBox="0 0 593 86" role="img" aria-label="APLEONA">
    <path
      fill={color}
      d="M130.6,1.46H95.85v82h16.52V58.19H128.9c19.68,0,32.44-11.18,32.44-28.43S149.3,1.46,130.6,1.46M129.5,44.1H112.37V15.55h16.76c10.2,0,16.4,5.35,16.4,14.21s-6.44,14.34-16,14.34m46.77,39.36h58.43V68.52H192.92V1.46H176.27Zm72.65,0h62.2V68.52H265.44V49.44h38.88V34.87H265.44V16.16h45.07V1.46H248.92ZM366.16,85c24.3,0,42.52-18.22,42.52-42.52S390.94,0,366.16,0c-24.3,0-42.52,18.22-42.52,42.52S341.37,85,366.16,85m0-14.94c-14.7,0-26-11.91-26-27.7s11.3-27.7,26-27.7,26,11.91,26,27.7-11.18,27.7-26,27.7"
    />
    <polygon
      fill={color}
      points="493.67 83.46 497.07 83.46 497.07 1.46 480.43 1.46 480.43 50.05 426.49 0.85 423.09 0.85 423.09 83.46 439.61 83.46 439.61 34.62"
    />
    <path fill={color} d="M574.34,83.46h18.59L551.13.85h-3.4L506.31,83.46h18.34l5.72-12.51h38.25Zm-25.15-53.7,13.55,27.58H536.23Z" />
    <path fill={color} d="M68,83.46H86.62L44.83.85h-3.4L0,83.46H18.34l5.72-12.51H62.31ZM42.88,29.76,56.43,57.34H29.93Z" />
  </svg>
);

export const Card: React.FC<{
  title?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  bodyless?: boolean;
  className?: string;
}> = ({ title, actions, children, bodyless, className }) => (
  <section className={`card ${className ?? ''}`}>
    {(title || actions) && (
      <header className="card-head">
        <h2>{title}</h2>
        <div className="row">{actions}</div>
      </header>
    )}
    {bodyless ? children : <div className="card-body">{children}</div>}
  </section>
);

export const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: 'accent' | 'warn-accent' | 'ok-accent';
}> = ({ label, value, hint, tone }) => (
  <div className={`card stat ${tone ?? ''}`}>
    <div className="label">{label}</div>
    <div className="value">{value}</div>
    {hint ? <div className="hint">{hint}</div> : null}
  </div>
);

export const Plate: React.FC<{ value: string }> = ({ value }) => (
  <span className="plate">{value}</span>
);

const STATUS_TONE: Record<ProtocolStatus, string> = {
  ENTWURF: 'grey',
  ABGESCHLOSSEN: 'blue',
  VERSENDET: 'green',
  VERSANDFEHLER: 'red',
};

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`badge ${STATUS_TONE[status as ProtocolStatus] ?? 'grey'}`}>
    {PROTOCOL_STATUS_LABEL[status as ProtocolStatus] ?? status}
  </span>
);

const TYPE_TONE: Record<string, string> = {
  NUTZERWECHSEL: 'blue',
  UEBERGABE: 'green',
  RUECKGABE: 'warn',
  POOL_AUSGABE: 'blue',
  POOL_RUECKNAHME: 'blue',
  ZUSTANDSCHECK: 'grey',
};

export const TypeBadge: React.FC<{ type: string }> = ({ type }) => (
  <span className={`badge ${TYPE_TONE[type] ?? 'grey'}`}>
    {PROTOCOL_TYPE_LABEL[type as ProtocolType] ?? type}
  </span>
);

export const VehicleStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const tone =
    status === 'VERFUEGBAR' ? 'green' : status === 'AUSGEGEBEN' ? 'blue' : status === 'WERKSTATT' ? 'warn' : 'grey';
  return <span className={`badge ${tone}`}>{VEHICLE_STATUS_LABEL[status as VehicleStatus] ?? status}</span>;
};

export const UsageBadge: React.FC<{ usage: string }> = ({ usage }) => (
  <span className={`badge ${usage === 'POOL' ? 'blue' : 'grey'}`}>
    {VEHICLE_USAGE_LABEL[usage as VehicleUsage] ?? usage}
  </span>
);

export const Empty: React.FC<{ icon?: string; text: string; hint?: string }> = ({
  icon = '∅',
  text,
  hint,
}) => (
  <div className="empty">
    <div className="big">{icon}</div>
    <div>{text}</div>
    {hint ? <div className="small" style={{ marginTop: 6 }}>{hint}</div> : null}
  </div>
);

export const Loading: React.FC<{ text?: string }> = ({ text = 'Lade …' }) => (
  <div className="empty">{text}</div>
);

export const KV: React.FC<{ rows: [string, React.ReactNode][] }> = ({ rows }) => (
  <div className="kv">
    {rows.map(([k, v], i) => (
      <React.Fragment key={`${k}-${i}`}>
        <div>{k}</div>
        <div>{v ?? '—'}</div>
      </React.Fragment>
    ))}
  </div>
);

export { formatDate, formatDateTime, formatKm };
